import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, Cell,
} from 'recharts';
import { TrendingUp, Users, Award, BookOpen, ChevronDown, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const COLORS = ['#4f46e5', '#7c3aed', '#2563eb', '#0891b2', '#059669', '#d97706', '#dc2626'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-bold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-semibold">
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}{p.unit || '%'}
        </p>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const [terms, setTerms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/academics/current-session'), api.get('/academics/classes')])
      .then(([sesRes, clsRes]) => {
        const tList = sesRes.data?.terms_for_year || [];
        setTerms(tList);
        if (sesRes.data?.term) setSelectedTerm(sesRes.data.term.id);
        const cList = clsRes.data || [];
        setClasses(cList);
        if (cList.length > 0) setSelectedClass(cList[0].id);
      })
      .catch(() => toast.error('Failed to load options'));
  }, []);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedTerm) params.set('term_id', selectedTerm);
      if (selectedClass) params.set('class_id', selectedClass);
      const res = await api.get(`/exams/analytics?${params.toString()}`);
      if (res.success) setData(res.data);
    } catch (err) {
      toast.error(err?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [selectedTerm, selectedClass]);

  useEffect(() => {
    if (selectedTerm || selectedClass) loadAnalytics();
  }, [selectedTerm, selectedClass, loadAnalytics]);

  const topClass = data?.class_overview?.reduce((a, b) => (b.avg_score > (a?.avg_score || 0) ? b : a), null);
  const topSubject = data?.subject_breakdown?.reduce((a, b) => (b.avg_score > (a?.avg_score || 0) ? b : a), null);
  const overallAvg = data?.class_overview?.length
    ? (data.class_overview.reduce((s, c) => s + c.avg_score, 0) / data.class_overview.length).toFixed(1)
    : '—';

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="analytics-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900">Performance Analytics</h1>
        <p className="text-sm text-slate-500 mt-0.5">School-wide academic performance insights</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex flex-wrap gap-4 items-center">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Term</label>
          <div className="relative">
            <select value={selectedTerm} onChange={e => setSelectedTerm(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-8 min-w-[160px]"
              data-testid="analytics-term-select">
              <option value="">All Terms</option>
              {terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Class (for breakdown &amp; trend)</label>
          <div className="relative">
            <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-8 min-w-[160px]"
              data-testid="analytics-class-select">
              <option value="">All Classes</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
        {loading && <Loader2 className="w-5 h-5 text-indigo-600 animate-spin mt-4" />}
      </div>

      {!data && !loading && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-12 text-center">
          <TrendingUp className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">Select a term or class to view analytics</p>
        </div>
      )}

      {data && (
        <>
          {/* Summary KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard icon={TrendingUp} label="School Avg Score" value={`${overallAvg}%`} color="indigo" testid="kpi-avg-score" />
            <KpiCard icon={Award} label="Top Class" value={topClass?.class_name || '—'} sub={topClass ? `${topClass.avg_score}%` : ''} color="violet" testid="kpi-top-class" />
            <KpiCard icon={BookOpen} label="Top Subject" value={topSubject?.subject_name || '—'} sub={topSubject ? `${topSubject.avg_score}%` : ''} color="blue" testid="kpi-top-subject" />
            <KpiCard icon={Users} label="Classes with Data" value={data.class_overview.length} color="emerald" testid="kpi-class-count" />
          </div>

          {/* Class Overview Chart */}
          {data.class_overview.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-1">Class Overview — Average Score</h3>
              <p className="text-xs text-slate-500 mb-4">{terms.find(t => t.id === selectedTerm)?.name || 'All Terms'}</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.class_overview} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="class_name" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="avg_score" name="Avg Score" radius={[6, 6, 0, 0]}>
                    {data.class_overview.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {/* Data table below chart */}
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 text-xs font-bold text-slate-500 uppercase">Class</th>
                      <th className="text-center py-2 text-xs font-bold text-slate-500 uppercase">Avg Score</th>
                      <th className="text-center py-2 text-xs font-bold text-slate-500 uppercase">Pass Rate</th>
                      <th className="text-center py-2 text-xs font-bold text-slate-500 uppercase">Students</th>
                      <th className="text-center py-2 text-xs font-bold text-slate-500 uppercase">Subjects</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.class_overview.map((cls, i) => (
                      <tr key={cls.class_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                            <span className="font-semibold text-slate-700">{cls.class_name}</span>
                          </div>
                        </td>
                        <td className="text-center">
                          <ScoreBadge score={cls.avg_score} />
                        </td>
                        <td className="text-center text-slate-600">{cls.pass_rate}%</td>
                        <td className="text-center text-slate-500">{cls.student_count}</td>
                        <td className="text-center text-slate-500">{cls.subject_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Subject Breakdown + Term Trend (side by side) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            {/* Subject Breakdown */}
            {data.subject_breakdown.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="subject-breakdown">
                <h3 className="font-bold text-slate-800 mb-0.5">Subject Breakdown</h3>
                <p className="text-xs text-slate-500 mb-4">
                  {classes.find(c => c.id === selectedClass)?.name} · {terms.find(t => t.id === selectedTerm)?.name || 'All Terms'}
                </p>
                <div className="space-y-3">
                  {[...data.subject_breakdown].sort((a, b) => b.avg_score - a.avg_score).map((s, i) => (
                    <div key={s.subject_name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700">{s.subject_name}</span>
                        <span className="text-sm font-bold" style={{ color: COLORS[i % COLORS.length] }}>{s.avg_score}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${s.avg_score}%`, background: COLORS[i % COLORS.length] }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Term Trend */}
            {data.term_trend.length > 1 && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="term-trend">
                <h3 className="font-bold text-slate-800 mb-0.5">Term Trend</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Avg score over terms — {classes.find(c => c.id === selectedClass)?.name}
                </p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.term_trend} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="term_name" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="avg_score" name="Avg Score" stroke="#4f46e5"
                      strokeWidth={2.5} dot={{ fill: '#4f46e5', r: 5 }} activeDot={{ r: 7 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {data.term_trend.length <= 1 && selectedClass && (
              <div className="bg-slate-50 rounded-xl border border-slate-100 p-8 flex flex-col items-center justify-center">
                <TrendingUp className="w-8 h-8 text-slate-300 mb-2" />
                <p className="text-sm text-slate-400 text-center">Term trend will appear when data for 2+ terms is available</p>
              </div>
            )}
          </div>

          {data.class_overview.length === 0 && !loading && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
              <AlertCircle className="w-9 h-9 mx-auto mb-3 text-amber-400" />
              <p className="font-semibold text-amber-800">No score data found</p>
              <p className="text-sm text-amber-600 mt-1">Enter exam results first, then come back to view analytics.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color, testid }) {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    violet: 'bg-violet-50 text-violet-700 border-violet-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.indigo}`} data-testid={testid}>
      <Icon className="w-5 h-5 mb-2 opacity-70" />
      <p className="text-2xl font-extrabold leading-tight">{value}</p>
      {sub && <p className="text-sm font-semibold opacity-80">{sub}</p>}
      <p className="text-xs font-medium opacity-60 mt-0.5 uppercase tracking-wide">{label}</p>
    </div>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 60 ? 'text-green-700 bg-green-50' : score >= 45 ? 'text-amber-700 bg-amber-50' : 'text-red-700 bg-red-50';
  return (
    <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>{score}%</span>
  );
}
